import type { ApiEndpoint } from "@/shared/api-endpoint";
import type { Complexity } from "@/shared/modal.api";
import {
    isNullableBooleanArray,
    isNumberArray,
    isComplexityArray,
    isObject,
    isStringArray,
} from "./utils/typeGuards";

export const kBlendRoute = "/blend";

export type BlendChromaMode = "vote" | "blend";

const isChromaModeArray = (value: unknown): value is BlendChromaMode[] =>
    Array.isArray(value) && value.every((v) => v === "vote" || v === "blend");

export interface BlendRequestBody {
    audio1Keys: string[];
    audio2Keys: string[];
    bpms: number[];
    blendValues: number[];
    chromaBlendModes: BlendChromaMode[];
    argmaxes: (boolean | null)[];
    complexities: Complexity[];
}

export interface BlendSuccessResponse {
    ok: true;
    downloadUrls: string[];
}

export const isBlendRequestBody = (value: unknown): value is BlendRequestBody => {
    if (
        !isObject(value) ||
        !isStringArray(value.audio1Keys) ||
        !isStringArray(value.audio2Keys) ||
        !isNumberArray(value.bpms) ||
        !isNumberArray(value.blendValues) ||
        !isChromaModeArray(value.chromaBlendModes)
    ) {
        return false;
    }

    const batchSize = value.audio1Keys.length;
    if (
        value.audio2Keys.length !== batchSize ||
        value.bpms.length !== batchSize ||
        value.blendValues.length !== batchSize ||
        value.chromaBlendModes.length !== batchSize
    ) {
        return false;
    }

    if (!isNullableBooleanArray(value.argmaxes) || value.argmaxes.length !== batchSize) {
        return false;
    }
    if (!isComplexityArray(value.complexities) || value.complexities.length !== batchSize) {
        return false;
    }

    return true;
};

export const isBlendSuccessResponse = (response: unknown): response is BlendSuccessResponse =>
    isObject(response) && response.ok === true && isStringArray(response.downloadUrls);

export const kBlendEndpoint: ApiEndpoint<BlendRequestBody, BlendSuccessResponse> = {
    route: kBlendRoute,
    label: "Blend",
    source: "Modal",
    category: "SpliceGen v2.0",
    description:
        "Blends two audio loops into a single harmonically coherent output using pitch-palette conditioning.",
    isRequestBody: isBlendRequestBody,
    isSuccessResponse: isBlendSuccessResponse,
};
